<template>
  <BGModal :shown="dialogShown"
           :title="$t('admin_event.add_title')">
    <template #content>
      <v-container fluid class="add-event">
        <v-form v-model="isFormValid"
                ref="addEventForm">
          <v-row class="pt-0">
            <v-col>
              <v-input :rules="requiredRules"
                       hide-details="auto"
                       v-model="event.name_en">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.name_en') }}</legend>
                  <TinyMceEditor v-model="event.name_en"></TinyMceEditor>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row class="pt-0">
            <v-col>
              <v-input :rules="requiredRules"
                       hide-details="auto"
                       v-model="event.name_zh">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.name_zh') }}</legend>
                  <TinyMceEditor v-model="event.name_zh"></TinyMceEditor>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-select outlined
                        :label="$t('admin_event.category')"
                        :items="categoryOptions"
                        v-model="event.category_id"
                        :rules="requiredRules"></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-radio-group :label="$t('admin_event.signup_mode')"
                             v-model="event.signup_mode"
                             :rules="requiredRules"
                             hide-details="auto">
                <v-radio :label="$t('event_signup_mode.disabled')"
                         :value="-1"></v-radio>
                <v-radio :label="$t('event_signup_mode.single_session')"
                         :value="1"></v-radio>
                <v-radio :label="$t('event_signup_mode.multi_session')"
                         :value="2"></v-radio>
                <v-radio :label="$t('event_signup_mode.multi_series')"
                         :value="3"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row v-if="event.signup_mode === -1 || event.signup_mode === 1"
                 class="mt-4">
            <v-col>
              <DatePickerInput :label="$t('admin_event.date')"
                               v-model="event.date"
                               :rules="requiredRules"
                               hide-details="auto"></DatePickerInput>
            </v-col>
          </v-row>
          <v-row v-if="event.signup_mode === -1 || event.signup_mode === 1">
            <v-col>
              <TimePickerInput outlined
                               :label="$t('admin_event.start_time')"
                               v-model="event.from_time"
                               :rules="requiredRules"
                               hide-details="auto"></TimePickerInput>
            </v-col>
            <v-col>
              <TimePickerInput outlined
                               :label="$t('admin_event.end_time')"
                               v-model="event.to_time"
                               :rules="requiredRules"
                               hide-details="auto"></TimePickerInput>
            </v-col>
          </v-row>
          <v-row v-if="event.signup_mode !== -1">
            <v-col>
              <v-input :rules="requiredRules"
                       hide-details="auto"
                       v-model="event.signup_until">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.signup_until') }}</legend>
                  <DateTimePickerInput v-model="event.signup_until"
                                       hide-details></DateTimePickerInput>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-input :rules="[
                              value => (visibleForever || !!value) || $t('register.required_fields'),
                            ]"
                       hide-details="auto"
                       v-model="event.visible_until">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.visible_until') }}</legend>
                  <v-row>
                    <v-col cols="2">
                      <v-checkbox :label="$t('admin_event.visible_forever')"
                                  v-model="visibleForever"></v-checkbox>
                    </v-col>
                    <v-col>
                      <DateTimePickerInput v-model="event.visible_until"
                                           hide-details
                                           :disabled="visibleForever"></DateTimePickerInput>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-radio-group :label="$t('admin_event.fee')"
                             v-model="feeMode"
                             :rules="requiredRules"
                             hide-details="auto">
                <v-radio :label="$t('event.free')"
                         :value="'FREE'"></v-radio>
                <v-radio :label="$t('admin_event.fixed_fee')"
                         :value="'FIXED'"></v-radio>
                <v-radio :label="$t('admin_event.fee_with_tiers')"
                         :value="'WITH_TIERS'"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row v-if="feeMode === 'FIXED'">
            <v-col>
              <v-text-field outlined
                            :label="$t('admin_event.fee')"
                            v-model="fee"
                            type="number"
                            prefix="HKD"
                            :rules="fixedFeeRules"
                            hide-details="auto"></v-text-field>
            </v-col>
          </v-row>
          <v-row v-if="feeMode === 'WITH_TIERS'">
            <v-col>
              <v-input :rules="feeTierRules"
                       v-model="feeTiers">
                <v-container fluid class="flex-column pa-0">
                  <legend class="v-label theme--light">{{ $t('admin_event.fee_tier') }}</legend>
                  <v-row v-for="(tier, idx) in feeTiers"
                         :key="idx">
                    <v-col>
                      <v-text-field outlined
                                    :label="$t('admin_event.fee_tier_en')"
                                    v-model="tier.name_en"
                                    :rules="requiredRules"
                                    hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field outlined
                                    :label="$t('admin_event.fee_tier_zh')"
                                    v-model="tier.name_zh"
                                    :rules="requiredRules"
                                    hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field outlined
                                    :label="$t('admin_event.fee')"
                                    v-model="tier.fee"
                                    :rules="requiredRules"
                                    hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field outlined
                                    :label="$t('admin_event.fee_tier_max')"
                                    v-model="tier.max"
                                    :rules="requiredRules"
                                    hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="1" class="align-content-center">
                      <v-btn icon @click="removeFeeTier(idx)">
                        <v-icon>fas fa-times</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-btn block @click="addFeeTier">{{ $t('admin_event.add_fee_tier') }}</v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row v-if="event.signup_mode === 1"
                 class="mt-4">
            <v-col>
              <v-text-field outlined
                            :label="$t('admin_event.quota')"
                            type="number"
                            v-model="event.quota"
                            :rules="[
                              value => (unlimitedQuota || !!value) || $t('register.required_fields'),
                            ]"
                            hide-details="auto"
                            :disabled="unlimitedQuota">
              </v-text-field>
            </v-col>
            <v-col cols="2">
              <v-checkbox :label="$t('unlimited')"
                          v-model="unlimitedQuota"></v-checkbox>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field outlined
                            :label="$t('admin_event.location_en')"
                            v-model="event.location_en"
                            :rules="requiredRules"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field outlined
                            :label="$t('admin_event.location_zh')"
                            v-model="event.location_zh"
                            :rules="requiredRules"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-input :rules="[value => value.length !== 0 || $t('register.required_fields')]"
                       hide-details="auto"
                       v-model="event.languages">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.languages') }}</legend>
                  <v-row>
                    <v-col>
                      <v-checkbox v-model="event.languages"
                                  :label="$t('languages.en')"
                                  value="en"
                                  hide-details></v-checkbox>
                    </v-col>
                    <v-col>
                      <v-checkbox v-model="event.languages"
                                  :label="$t('languages.zh-Hant')"
                                  value="zh-Hant"
                                  hide-details></v-checkbox>
                    </v-col>
                    <v-col>
                      <v-checkbox v-model="event.languages"
                                  :label="$t('languages.zh-Hans')"
                                  value="zh-Hans"
                                  hide-details></v-checkbox>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row class="pt-0">
            <v-col>
              <v-input hide-details
                       v-model="event.remarks_en">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.remarks_en') }}</legend>
                  <TinyMceEditor v-model="event.remarks_en"></TinyMceEditor>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row class="pt-0">
            <v-col>
              <v-input hide-details
                       v-model="event.remarks_zh">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.remarks_zh') }}</legend>
                  <TinyMceEditor v-model="event.remarks_zh"></TinyMceEditor>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-input hide-details="auto"
                       v-model="banner"
                       :rules="requiredRules">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.banner') }}</legend>
                  <PhotoPreviewInput v-model="banner"
                                     div-class="event-banner"
                                     file-name="event-banner"></PhotoPreviewInput>
                  <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG; RECOMMENDED SIZE: <b>2545px(W) X 1081px(H)</b></small>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row class="pt-0">
            <v-col>
              <v-input hide-details
                       v-model="event.description_en">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.description_en') }}</legend>
                  <TinyMceEditor v-model="event.description_en"></TinyMceEditor>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row class="pt-0">
            <v-col>
              <v-input hide-details
                       v-model="event.description_zh">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.description_zh') }}</legend>
                  <TinyMceEditor v-model="event.description_zh"></TinyMceEditor>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-file-input :label="$t('admin_event.poster')"
                            accept="image/jpeg, image/png, image/jpg, application/pdf"
                            hint="ACCEPTED FILE FORMAT: PNG, JPEG, JPG, PDF"
                            persistent-hint
                            clearable
                            v-model="poster">
                <template #selection="{file, text}">
                  {{ text || file }}
                </template>
                <template #append-outer>
                  <a :href="getURL(poster)"
                     target="_blank"
                     v-if="poster">
                    <v-icon>fas fa-search</v-icon>
                  </a>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-file-input :label="$t('admin_event.attachment')"
                            accept="image/jpeg, image/png, image/jpg, application/pdf"
                            hint="ACCEPTED FILE FORMAT: PNG, JPEG, JPG, PDF"
                            persistent-hint
                            clearable
                            v-model="attachment">
                <template #selection="{file, text}">
                  {{ text || file }}
                </template>
                <template #append-outer>
                  <a :href="getURL(attachment)"
                     target="_blank"
                     v-if="attachment">
                    <v-icon>fas fa-search</v-icon>
                  </a>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-input hide-details="auto">
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_event.images') }}</legend>
                  <!--                  <v-file-input :label="$t('admin_event.images')"-->
                  <!--                                accept="image/jpeg, image/png, image/jpg"-->
                  <!--                                ref="imageFileInput"-->
                  <!--                                @input="onImageUpload"></v-file-input>-->
                  <input type="file"
                         accept="image/jpeg, image/png, image/jpg"
                         hidden
                         ref="imageFileInput"
                         @input="onImageUpload">
                  <PhotoCarousel v-model="images"
                                 show-placeholder
                                 @placeholder="$refs.imageFileInput.click()"
                                 @delete="deleteImage"></PhotoCarousel>
                  <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG; RECOMMENDED SIZE: <b>1013(W) X 1287(H)</b> (MEGA EVENT: <b>2371(W) X 787 (H)</b>)</small>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </template>


    <template #footer>
      <v-row no-gutters>
        <v-btn @click="cancel"
               class="mr-3"
               depressed
               large>
          {{ $t('cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary"
               depressed
               large
               @click="($refs.addEventForm.validate() && save()) || $showAlertMessage($t('admin_event.invalid_form_msg'))">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
      </v-row>
    </template>
  </BGModal>
</template>
<script>
import {DateTime} from 'luxon';
import BGModal from "@/components/BGModal";
import {computed, ref, watch} from "@vue/composition-api";
import TinyMceEditor from "@/components/TinyMceEditor";
import DatePickerInput from "@/components/DatePickerInput";
import DateTimePickerInput from "@/components/DateTimePickerInput";
import PhotoPreviewInput from "@/components/PhotoPreviewInput";
import PhotoCarousel from "@/components/PhotoCarousel";
import TimePickerInput from "@/components/TimePickerInput";
import _ from "lodash";
import uuid4 from "uuid/v4";

export default {
  name: 'AddEventModal',
  components: {TimePickerInput, PhotoCarousel, PhotoPreviewInput, DateTimePickerInput, DatePickerInput, TinyMceEditor, BGModal},
  props: {
    shown: Boolean,
  },
  setup(props, {root, emit}) {
    const dialogShown = ref(props.shown);

    // Init
    watch(() => props.shown, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        dialogShown.value = newValue;

        if (newValue) {
          event.value = {
            languages: [],
          };
          getCategories();
        }
      }
    });
    const cancel = function () {
      emit('cancel');
    };

    const getURL = function (f) {
      if (f instanceof File) {
        return URL.createObjectURL(f);
      } else {
        return 'api/static/' + f;
      }
    };

    // Form
    const isFormValid = ref(false);

    const event = ref({});

    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);

    const categories = ref([]);
    const categoryOptions = computed(() => {
      return categories.value.map((c) => {
        return {text: c.name, value: c.id};
      })
    })
    const getCategories = function () {
      root.$callAPI('hkiarace/event_category/', {}, function (response) {
        categories.value = response.body.categories;
      });
    };

    const visibleForever = ref(false);

    const feeMode = ref(null);
    const fee = ref(null);
    const fixedFeeRules = ref([
      value => (feeMode.value !== 'FIXED' || !!value) || root.$t('register.required_fields'),
    ])
    const feeTiers = ref([{id: uuid4(), name_en: null, name_zh: null, fee: null, max: null}]);
    const feeTierRules = ref([
      value => (feeMode.value !== 'WITH_TIERS' || !!value.length) || root.$t('register.required_fields'),
    ])
    const addFeeTier = function () {
      feeTiers.value = [
        ...feeTiers.value,
        {id: uuid4(), name_en: null, name_zh: null, fee: null, max: null},
      ]
    }
    const removeFeeTier = function (idx) {
      feeTiers.value.splice(idx, 1);
      if (feeTiers.value.length === 0) {
        feeTiers.value.push({id: uuid4(), name_en: null, name_zh: null, fee: null, max: null});
      }
    }

    const unlimitedQuota = ref(false);

    const banner = ref(null);
    const poster = ref(null);
    const attachment = ref(null);
    const images = ref([]);
    const imageFileInput = ref(null);
    const onImageUpload = function (e) {
      let input = e.target;
      let f = input.files[0];
      images.value.push(f);
      input.value = '';
    }
    const deleteImage = function (idx) {
      images.value.splice(idx, 1);
    };

    const save = async function () {
      let start = event.value.visible_from;
      let end = event.value.signup_until;
      if (event.value.signup_mode === -1 || event.value.signup_mode === 1) {
        let date = DateTime.fromISO(event.value.date);
        let from_time = DateTime.fromISO(event.value.from_time);
        let to_time = DateTime.fromISO(event.value.to_time);

        start = DateTime.fromObject({
          year: date.year,
          month: date.month,
          day: date.day,
          hour: from_time.hour,
          minute: from_time.minute,
        }).toISO();

        end = DateTime.fromObject({
          year: date.year,
          month: date.month,
          day: date.day,
          hour: to_time.hour,
          minute: to_time.minute,
        }).toISO();
      }

      let fee_tiers = '[]';
      if (feeMode.value === 'FIXED') {
        fee_tiers = JSON.stringify([{name_en: null, name_zh: null, fee: this.fee}]);
      } else if (feeMode.value === 'WITH_TIERS') {
        fee_tiers = JSON.stringify(_.filter(feeTiers.value, function (t) {
          return t.name_en && t.name_en.length && t.fee;
        }));
      }

      let params = {
        ...event.value,

        start,
        end,
        visible_until: visibleForever.value ? null : event.value.visible_until,
        signup_from: event.value.signup_from,
        signup_until: event.value.signup_until,
        quota: unlimitedQuota.value ? -1 : event.value.quota,
        languages: JSON.stringify(event.value.languages),
        fee_tiers,

        banner: await root.$convertFileToBase64(banner.value),
        poster: poster.value ? await root.$convertFileToBase64(poster.value) : null,
        attachment: attachment.value ? await root.$convertFileToBase64(attachment.value) : null,
        // images: images.value.map(async (i) => await root.$convertFileToBase64(i)),
        images: await Promise.all(images.value.map(async (i) => {
          if (i instanceof File) {
            return await root.$convertFileToBase64(i)
          } else {
            return i;
          }
        })),
      }
      root.$callAPI('hkiarace/admin/event/create/', params, () => {
        emit('finish');
      });
    }

    return {
      dialogShown,
      cancel,

      getURL,

      isFormValid,
      event,

      requiredRules,

      visibleForever,

      feeMode,
      fee,
      fixedFeeRules,
      feeTiers,
      feeTierRules,
      addFeeTier,
      removeFeeTier,

      unlimitedQuota,

      banner,
      poster,
      attachment,
      images,
      imageFileInput,
      onImageUpload,
      deleteImage,

      categoryOptions,

      save,
    }
  }
}
</script>

<style lang="less">
.add-event {
  .v-input {
    legend {
      font-weight: bold;
    }
  }
}
</style>
